import React, { useEffect, useState } from 'react';
import api from '../../../utils/api';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { useToast } from '../../../hooks/use-toast';

const TYPES = [
  { value: 'fixed', label: 'Fixed Incentive' },
  { value: 'lead_conversion', label: 'Lead Conversion Bonus' },
  { value: 'admission', label: 'Admission Bonus' },
  { value: 'monthly_performance', label: 'Monthly Performance Bonus' },
  { value: 'referral', label: 'Referral Bonus' },
  { value: 'manual', label: 'Manual Bonus' },
];

export default function CrmIncentiveManager() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({
    employee_id: '', type: 'manual', amount: '', notes: '', payroll_period_start: '',
  });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [iRes, eRes] = await Promise.all([
        api.get('/crm/payroll/incentives'),
        api.get('/crm/employees'),
      ]);
      setItems(iRes.data?.data?.items || []);
      setEmployees((eRes.data?.data?.items || []).filter((e) => e.status === 'active'));
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    try {
      await api.post('/crm/payroll/incentives', {
        ...form,
        employee_id: Number(form.employee_id),
        amount: Number(form.amount),
        payroll_period_start: form.payroll_period_start || null,
      });
      toast({ title: 'Added' });
      setForm({ employee_id: '', type: 'manual', amount: '', notes: '', payroll_period_start: '' });
      load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' });
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this incentive?')) return;
    try {
      await api.delete(`/crm/payroll/incentives/${id}`);
      load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' });
    }
  };

  if (loading) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle className="text-base">Add Incentive</CardTitle></CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <Label>Employee</Label>
            <Select value={form.employee_id} onValueChange={(v) => setForm((f) => ({ ...f, employee_id: v }))}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {employees.map((e) => (
                  <SelectItem key={e.id} value={String(e.id)}>
                    {e.first_name} {e.last_name} ({e.employee_code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Type</Label>
            <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Amount</Label>
            <Input type="number" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} />
          </div>
          <div>
            <Label>Period Start</Label>
            <Input type="date" value={form.payroll_period_start} onChange={(e) => setForm((f) => ({ ...f, payroll_period_start: e.target.value }))} />
          </div>
          <div className="sm:col-span-2">
            <Label>Notes</Label>
            <Input value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
          </div>
          <div className="flex items-end">
            <Button onClick={create}>Add Incentive</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Incentive Records</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left">
                <th className="py-2">Employee</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Period</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id} className="border-b">
                  <td className="py-2">{row.first_name} {row.last_name}</td>
                  <td>{row.type}</td>
                  <td>₹{Number(row.amount).toLocaleString('en-IN')}</td>
                  <td>{row.payroll_period_start || '—'}</td>
                  <td>{row.status}</td>
                  <td>
                    <Button size="sm" variant="ghost" onClick={() => remove(row.id)}>Delete</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
